import i18n from '@/utils/i18n';
import { FinanceHubInvoiceSummaryQuery, InvoiceListAgingBucket } from '@/types';
import { formatCurrency } from '@/utils/money';
import { InvoiceSummary } from '@finance-hub/domain/invoicing/blocs/types/invoice-summary';

type InvoiceSummaryResponse =
  FinanceHubInvoiceSummaryQuery['getUnpaidInvoiceSummary'];

const formatAmount = (amountInMicros: number, currencyCode: string) =>
  formatCurrency(amountInMicros, currencyCode, {
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

const getCreditCountLabel = (count: number) =>
  i18n.t(
    count === 1
      ? 'finance_hub.invoices.summary.count.credit'
      : 'finance_hub.invoices.summary.count.credits',
    { count },
  );

const formatCreditAmount = (amountInMicros: number, currencyCode: string) =>
  formatAmount(
    amountInMicros === 0 ? 0 : -Math.abs(amountInMicros),
    currencyCode,
  );

const getInvoiceCountLabel = (count: number) =>
  i18n.t(
    count === 1
      ? 'finance_hub.invoices.summary.count.invoice'
      : 'finance_hub.invoices.summary.count.invoices',
    { count },
  );

export const transformInvoiceSummary = (
  summary: InvoiceSummaryResponse,
): InvoiceSummary => {
  const currencyCode = summary.currency_code;

  return {
    amountBuckets: [
      {
        amount: formatAmount(summary.total_due.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.total_due.count),
        filter: { kind: 'allInvoices' },
        label: i18n.t('finance_hub.invoices.summary.total_due'),
        variant: 'neutral',
      },
      {
        amount: formatAmount(summary.overdue.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.overdue.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Overdue,
        },
        label: i18n.t('finance_hub.invoices.summary.overdue'),
        variant: 'overdue',
      },
      {
        amount: formatCreditAmount(
          summary.total_credits.amount_in_micros,
          currencyCode,
        ),
        countLabel: getCreditCountLabel(summary.total_credits.count),
        filter: { kind: 'creditMemos' },
        label: i18n.t('finance_hub.invoices.summary.total_credits'),
        variant: 'credit',
      },
    ],
    timeSeriesBuckets: [
      {
        amount: formatAmount(summary.current.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.current.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Current,
        },
        label: i18n.t('finance_hub.invoices.summary.current'),
        variant: 'current',
      },
      {
        amount: formatAmount(summary.days_0_14.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.days_0_14.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Days_0_14,
        },
        label: i18n.t('finance_hub.invoices.summary.age_0_14'),
        variant: 'warning',
      },
      {
        amount: formatAmount(summary.days_15_30.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.days_15_30.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Days_15_30,
        },
        label: i18n.t('finance_hub.invoices.summary.age_15_30'),
        variant: 'warning',
      },
      {
        amount: formatAmount(summary.days_31_60.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.days_31_60.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Days_31_60,
        },
        label: i18n.t('finance_hub.invoices.summary.age_31_60'),
        variant: 'error',
      },
      {
        amount: formatAmount(summary.days_61_90.amount_in_micros, currencyCode),
        countLabel: getInvoiceCountLabel(summary.days_61_90.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Days_61_90,
        },
        label: i18n.t('finance_hub.invoices.summary.age_61_90'),
        variant: 'critical',
      },
      {
        amount: formatAmount(
          summary.days_90_plus.amount_in_micros,
          currencyCode,
        ),
        countLabel: getInvoiceCountLabel(summary.days_90_plus.count),
        filter: {
          kind: 'agingBucket',
          overdueBucket: InvoiceListAgingBucket.Days_90Plus,
        },
        label: i18n.t('finance_hub.invoices.summary.age_90_plus'),
        variant: 'critical',
      },
    ],
  };
};
